<template>
  <div class="relative" :class="compact ? '' : 'h-[295px] rounded-xl'" ref="containerRef">
    <!-- radar background from first selected map -->
    <div v-if="!compact" class="absolute inset-0 rounded-xl border border-gray-800 overflow-hidden bg-gray-900">
      <img
        src="../../assets/dust2-outline.svg"
        class="absolute inset-0 w-full h-full object-cover"
        alt=""
      />
    </div>

    <!-- header -->
    <div v-if="!compact" class="relative z-10 flex items-center justify-between px-4 pt-4 pb-1">
      <p class="text-xs uppercase tracking-widest text-gray-400 font-bold">{{ $t('play.matchmaking.maps') }}</p>
      <p v-if="!single" class="text-xs uppercase tracking-widest text-gray-400 font-bold">{{ $t('play.matchmaking.standard_pool') }}</p>
    </div>

    <!-- selected map medallions -->
    <div v-if="!compact" class="relative z-10 grid grid-cols-4 gap-x-2 gap-y-3 px-4 py-2">
      <button
        v-for="csMap in csMaps"
        :key="csMap.name"
        @click="toggleMap(csMap)"
        :disabled="disabled"
        class="flex flex-col items-center gap-1 group"
        :class="disabled ? 'cursor-not-allowed' : ''"
        :title="csMap.friendly_name"
      >
        <img
          :src="pinUrl(csMap.name)"
          class="w-11 h-11 object-contain transition-all duration-150"
          :class="modelValue.includes(csMap.name)
            ? 'opacity-100 drop-shadow-lg'
            : 'opacity-25 grayscale brightness-75'"
          :alt="csMap.friendly_name"
        />
        <span
          class="text-[10px] leading-none transition-colors text-center"
          :class="modelValue.includes(csMap.name) ? 'text-gray-300' : 'text-gray-600'"
        >
          {{ csMap.friendly_name }}
        </span>
      </button>
    </div>

    <!-- footer toggle -->
    <div class="z-50" :class="compact ? 'relative' : 'absolute bottom-2 left-2 right-2'">
      <button
        @click="toggleOpen"
        :disabled="disabled"
        class="w-full flex items-center justify-between rounded-md backdrop-blur-[1.5px] px-4 py-3 "
        :class="[disabled ? 'cursor-not-allowed' : '', compact ? 'border border-gray-800 bg-gray-900 h-14 gap-3' : '']"
      >
        <span class="font-semibold text-base flex items-center gap-2" :class="disabled ? 'text-gray-400' : 'text-text-primary'">
          <img v-if="compact && selectedMaps[0]" :src="pinUrl(selectedMaps[0].name)" alt="" class="w-6 h-6 object-contain" />{{ footerLabel }}
        </span>
        <LockClosedIcon v-if="disabled" class="h-4 w-4 text-gray-500 flex-none" aria-hidden="true" />
        <ChevronDownIcon v-else class="h-5 w-5 text-gray-400 transition-transform" :class="open ? 'rotate-180' : ''" />
      </button>

      <transition
        enter-active-class="transition ease-out duration-100"
        enter-from-class="transform opacity-0 scale-95"
        enter-to-class="transform opacity-100 scale-100"
        leave-active-class="transition ease-in duration-75"
        leave-from-class="transform opacity-100 scale-100"
        leave-to-class="transform opacity-0 scale-95"
      >
        <div
          v-if="open"
          class="absolute bottom-full left-0 mb-2 w-full origin-bottom rounded-lg border overflow-hidden"
          :class="compact ? 'min-w-[16rem]' : ''"
          style="background: #0F141A; border-color: #1E2733; box-shadow: 0px 8px 32px rgba(0,0,0,0.16);"
        >
          <div class="py-2" :class="single ? '' : 'overflow-y-auto max-h-64'">
            <button
              v-for="csMap in csMaps"
              :key="csMap.name"
              @click.stop="toggleMap(csMap)"
              :disabled="disabled"
              class="w-full flex items-center px-2 py-2 gap-3 h-9 rounded transition-colors text-left hover:bg-white/5"
            >
              <!-- checkbox, or a radio when one map is the rule -->
              <span
                class="flex-shrink-0 w-4 h-4 border flex items-center justify-center transition-colors"
                :class="single ? 'rounded-full' : 'rounded'"
                :style="modelValue.includes(csMap.name)
                  ? 'border-color: #6366f1; background: #6366f1'
                  : 'border-color: #374151; background: transparent'"
              >
                <CheckIcon v-if="modelValue.includes(csMap.name)" class="h-3 w-3 text-white" />
              </span>
              <!-- map badge 20×20 -->
              <img :src="pinUrl(csMap.name)" class="w-5 h-5 flex-shrink-0 object-contain" :alt="csMap.friendly_name" />
              <span
                class="text-sm font-medium"
                :style="modelValue.includes(csMap.name) ? 'color: #EFF2F5' : 'color: #9CADC4'"
              >{{ csMap.friendly_name }}</span>
            </button>
          </div>
          <div v-if="!single" class="px-3 py-2 text-xs font-medium" style="border-top: 1px solid #1E2733; color: #9CADC4;">
            {{ $t('play.matchmaking.maps_selected', { selected: modelValue.length, total: csMaps.length }) }}
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script setup>
import {
  ref, computed, watch, onMounted, onUnmounted,
} from 'vue';
import { ChevronDownIcon, CheckIcon, LockClosedIcon } from '@heroicons/vue/24/solid';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  csMaps: { type: Array, default: () => [] },
  modelValue: { type: Array, default: () => [] },
  // Locked while the party is queued, and for everyone who is not the leader.
  disabled: { type: Boolean, default: false },
  // One map at a time: a LAN party match is played on a single map.
  single: { type: Boolean, default: false },
  // Just the button with the drop-up list, for a toolbar.
  compact: { type: Boolean, default: false },
});
const emit = defineEmits(['update:modelValue']);
const { t } = useI18n();

const open = ref(false);
const containerRef = ref(null);

const selectedMaps = computed(() => props.csMaps.filter((m) => props.modelValue.includes(m.name)));

const radarBackground = computed(() => {
  const first = selectedMaps.value[0];
  return first ? `/maps/${first.name}_radar.svg` : null;
});

function pinUrl(mapName) {
  return `/maps/${mapName.toLowerCase()}_pin.png`;
}

function toggleOpen() {
  if (props.disabled) return;
  open.value = !open.value;
}

const footerLabel = computed(() => {
  if (!props.single) return t('play.matchmaking.maps_selected', { selected: props.modelValue.length, total: props.csMaps.length });
  return selectedMaps.value[0]?.friendly_name || t('play.matchmaking.select_one_map');
});

function toggleMap(csMap) {
  if (props.disabled) return;
  if (props.single) {
    emit('update:modelValue', [csMap.name]);
    open.value = false;
    return;
  }
  const next = props.modelValue.includes(csMap.name)
    ? props.modelValue.filter((n) => n !== csMap.name)
    : [...props.modelValue, csMap.name];
  emit('update:modelValue', next);
}

function onClickOutside(e) {
  if (containerRef.value && !containerRef.value.contains(e.target)) open.value = false;
}

// Queueing can lock the panel while it is open — collapse it rather than
// leaving an open list the leader can no longer act on.
watch(() => props.disabled, (locked) => {
  if (locked) open.value = false;
});

onMounted(() => document.addEventListener('mousedown', onClickOutside));
onUnmounted(() => document.removeEventListener('mousedown', onClickOutside));
</script>

<style scoped>
.radar-bg {
  transform: rotate(30deg) scale(2.2);
  transform-origin: center;
}
</style>
